import { ComponentType, lazy, Suspense, useState } from "react";
import { Center, Loader } from "@mantine/core";
import { ContextModalProps } from "@mantine/modals";

/**
 * Wraps a context modal so its code is loaded only when the modal is opened for the first time.
 * Modals opened on app load should call `preload()` and wait for it before opening,
 * so the modal is rendered complete at once instead of showing a loader first (layout shift).
 */
export const lazyModal = <P extends ContextModalProps<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  loadModal: () => Promise<ComponentType<P>>
) => {
  let loadedModal: ComponentType<P> | undefined;
  let loadingModal: Promise<ComponentType<P>> | undefined;

  const preload = () => {
    loadingModal ??= loadModal().then((component) => {
      loadedModal = component;
      return component;
    });
    return loadingModal;
  };

  const LazyModal = lazy(() =>
    preload().then((component) => ({ default: component }))
  ) as unknown as ComponentType<P>;

  const LazyModalWrapper = (props: P) => {
    // Decided once per modal instance, switching it later would remount the modal
    const [PreloadedModal] = useState(() => loadedModal);

    if (PreloadedModal) {
      return <PreloadedModal {...props} />;
    }

    return (
      <Suspense
        fallback={
          <Center p="md">
            <Loader size="sm" />
          </Center>
        }
      >
        <LazyModal {...props} />
      </Suspense>
    );
  };

  return Object.assign(LazyModalWrapper, { preload });
};
